import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import { useOrganization } from '@/contexts/OrganizationContext'
import { BANKING_KEYS } from './useBanking'
import { CASH_POSITION_SUMMARIES_KEY } from './cashPositionSummaries'
import { resolveDateCashed, storedStatusFor } from '@/lib/sage-utils'
import type {
  SageSyncStateRow,
  SageCheckRow,
  SageBankAccountRow,
  SageCheckSortColumn,
  DcpCheckStatus,
} from '@/lib/db-tenant'

/**
 * Sage Intacct integration hooks (per-tenant architecture).
 *
 * Sage data lives in each tenant schema (tenant_<uuid>.sage_sync_state,
 * sage_bank_accounts, sage_checks). All read hooks scope queries by the
 * current organization id; mutations forward `organizationId` to the
 * `sage-sync` edge function which enforces auth and per-org access.
 *
 * `useSageSyncStatus` polls sage_sync_state every 3s while any row is
 * 'syncing'. After a manual trigger it keeps polling for a 15s grace
 * period waiting for the Trigger.dev task to flip the row, and trips a
 * 120s overall safety timeout. Sync types: 'checks' and 'bank_accounts'.
 */

/** Safety timeout for sync polling — 120 seconds */
const SYNC_POLLING_TIMEOUT_MS = 120_000

/**
 * Request timeout for the sage-sync edge function POST. Set high enough to
 * accommodate slow Supabase cold-starts (~10s) plus the auth + 409 path
 * (~1-2s), short enough that an actually-hung function surfaces as a
 * toast instead of locking the mutation indefinitely.
 */
const SAGE_SYNC_REQUEST_TIMEOUT_MS = 30_000

/** Polling interval when any sync_type is in progress — 3 seconds */
const SYNC_POLLING_INTERVAL_MS = 3_000

/**
 * Grace period after a sync trigger — poll for up to 15s waiting for the
 * Trigger.dev task to set sync_status = 'syncing' in DB. Closes the gap
 * between the manual button click and the task picking up the job.
 */
const TRIGGER_GRACE_PERIOD_MS = 15_000

/**
 * Staleness window for a 'pending' sage_sync_state row — 30 seconds.
 *
 * The `sage-sync` edge function sets sync_status = 'pending' the instant it
 * claims a sync (the row's update trigger refreshes `updated_at`), and the
 * Trigger.dev task only flips it to 'syncing' once it actually starts running.
 * A 'pending' row therefore means "claimed, waiting for the task to start".
 *
 * We treat such a row as in-progress ONLY while it is fresh — i.e. its
 * updated_at is within the last 30s. If the task never advances it to
 * 'syncing' within that window (queue back-pressure, failed enqueue), the
 * row is considered stale: the UI stops counting it as syncing (re-enabling
 * the action buttons) so it can't hang indefinitely the way a stuck
 * 'syncing' row used to.
 */
const PENDING_STALE_TIMEOUT_MS = 30_000

/**
 * Query keys for Sage data.
 * All keys include the organization id so cache entries are scoped per tenant.
 */
export const SAGE_KEYS = {
  /** Polled sync state from tenant.sage_sync_state */
  syncStatus: (orgId?: string) => ['sage', orgId, 'sync-status'] as const,
  /** AP check register rows from tenant.sage_checks */
  checks: (orgId?: string) => ['sage', orgId, 'checks'] as const,
  /** Bank account catalog rows from tenant.sage_bank_accounts */
  bankAccounts: (orgId?: string) => ['sage', orgId, 'bank-accounts'] as const,
  /**
   * Stores the timestamp (Date.now()) when a sync was last triggered.
   * Read by useSageSyncStatus to keep polling during the grace period
   * before the Trigger.dev task starts. Scoped per tenant.
   */
  syncTriggerTime: (orgId?: string) => ['sage', orgId, 'sync-trigger-time'] as const,
  /**
   * Stores the timestamp (Date.now()) when a PER-ACCOUNT balance sync was last
   * triggered. Read by useSageBankAccounts to keep polling sage_bank_accounts
   * during the grace period before the per-account Trigger.dev task flips a
   * row's balance_sync_status. Distinct from `syncTriggerTime` (which drives
   * the global sage_sync_state poll in useSageSyncStatus) so a per-account Sync
   * and a Resync All keep their grace windows independent. Scoped per tenant.
   */
  bankAccountsSyncTriggerTime: (orgId?: string) =>
    ['sage', orgId, 'bank-accounts-sync-trigger-time'] as const,
}

/**
 * Sort + dedupe an optional readonly string array for inclusion in a React
 * Query cache key. Two callers passing the same logical filter set in
 * different orders (`['A','C']` vs `['C','A']`) hit the same cache entry
 * instead of duplicating the fetch. Empty/undefined inputs collapse to
 * `undefined` so callers can spread the result into the queryKey without a
 * conditional.
 *
 * Extracted from the duplicated inline `[...new Set(arr)].sort()` blocks in
 * `useSageChecks` + `useSageChecksCount` so a future rule change (e.g.
 * casefold before dedupe, or sorting via a locale-aware collator) lands in
 * exactly one place and both query keys stay in lockstep. Drift between the
 * two would break the pagination math (count → totalPages diverges from
 * the page rows).
 */
function normalizeArrayForKey<T>(arr: readonly T[] | undefined): T[] | undefined {
  return arr?.length ? [...new Set(arr)].sort() : undefined
}

/** Discriminator values used in tenant.sage_sync_state.sync_type */
export type SageSyncType = 'checks' | 'bank_accounts' | 'balances' | 'facilities'

/**
 * Translates a sync_type discriminator into a human-readable label for toasts.
 */
function labelForSyncType(syncType: string): string {
  switch (syncType) {
    case 'checks':
      return 'checks sync'
    case 'bank_accounts':
      return 'bank accounts sync'
    case 'balances':
      return 'balances sync'
    case 'facilities':
      return 'facilities sync'
    default:
      return syncType
  }
}

/**
 * Whether a sage_sync_state row should count as "in progress" for the
 * purpose of disabling action buttons and keeping the status poll alive.
 *
 * A row is active when either:
 *   - sync_status === 'syncing' — the Trigger.dev task is actively running, OR
 *   - sync_status === 'pending' AND it is still fresh — the edge function has
 *     claimed the sync (the update trigger refreshes updated_at) but the task
 *     hasn't started yet. Freshness is bounded by PENDING_STALE_TIMEOUT_MS so a
 *     'pending' row the task never advances to 'syncing' auto-frees the UI.
 *
 * A 'pending' row with a null updated_at (shouldn't happen — updated_at is NOT
 * NULL) is treated as NOT active, failing safe toward re-enabling the UI rather
 * than hanging on a row we can't age out.
 */
export function isActiveSync(row: SageSyncStateRow): boolean {
  if (row.sync_status === 'syncing') return true
  if (row.sync_status === 'pending') {
    // Freshness is keyed off `updated_at` (refreshed by the row's update
    // trigger on the claim's flip to 'pending'), NOT `last_sync_at`.
    // `last_sync_at` is the delta-sync watermark — written only on a
    // successful completion — so for a freshly-claimed pending row it is the
    // previous successful sync (or null), which would wrongly read as stale.
    // `updated_at` is NOT NULL (defaults to now()), but keep a defensive guard.
    if (row.updated_at == null) return false
    return Date.now() - Date.parse(row.updated_at) < PENDING_STALE_TIMEOUT_MS
  }
  return false
}

/**
 * Whether a single sage_bank_accounts row's PER-ACCOUNT balance sync should
 * count as "in progress". This is the row-level analogue of {@link isActiveSync}
 * but reads the per-account lock columns instead of the global sage_sync_state
 * row, so one account syncing never disables the Sync buttons of its siblings.
 *
 * A row is account-active when either:
 *   - balance_sync_status === 'syncing' — the per-account Trigger.dev task is
 *     actively running for this account, OR
 *   - balance_sync_status === 'pending' AND it is still fresh — the edge
 *     function has claimed this account's sync (stamping balance_sync_locked_at
 *     = now) but the task hasn't started yet. Freshness is bounded by
 *     PENDING_STALE_TIMEOUT_MS so a 'pending' row the task never advances to
 *     'syncing' auto-frees the per-row button after the window.
 *
 * A 'pending' row with a null balance_sync_locked_at (shouldn't happen — the
 * edge always stamps it on claim) is treated as NOT active, failing safe toward
 * re-enabling the row rather than hanging on a lock we can't age out.
 */
export function isAccountBalanceSyncActive(row: SageBankAccountRow): boolean {
  if (row.balance_sync_status === 'syncing') return true
  if (row.balance_sync_status === 'pending') {
    if (row.balance_sync_locked_at == null) return false
    return Date.now() - Date.parse(row.balance_sync_locked_at) < PENDING_STALE_TIMEOUT_MS
  }
  return false
}

/**
 * POST to the `sage-sync` edge function for the given action.
 * Pulls the access token from the active Supabase session and forwards
 * the organization id in the body so the edge function can scope work
 * to the correct tenant. Returns the parsed JSON response.
 *
 * @param action - one of the supported sync-sync actions
 * @param organizationId - tenant the sync should target
 * @param financialEntityId - optional Sage BANKACCOUNTID; when present on a
 *   `sync-balances` call the edge function scopes the sync to that single
 *   account (AC#2 per-account Sync). Omit it to sync ALL accounts (AC#3).
 *
 * TODO(test-coverage): the 30s AbortController timeout path here is
 * intentionally not covered by unit tests yet — see review 18216
 * issue #74. The `clearTimeout(timeoutId)` call lives inside the
 * `finally` block below, so the success / network-error / abort
 * paths all share the same teardown branch; a regression in any of
 * them would surface as memory-leak DevTools breadcrumbs or stuck
 * "syncing" toasts in production. The timeout-path tests need
 * `vi.useFakeTimers()` + `vi.advanceTimersByTimeAsync(30000)` to
 * fire (same flake-risk shape as `useSageSyncStatus` per issue #24);
 * bundling them with the polling-hook coverage when one of #24's
 * triggers fires is the cheaper path. Until then, the
 * `useSyncSageChecks` / `useSyncSageBankAccounts` happy-path tests
 * in `useSageIntegration.test.tsx` cover the consuming layer.
 */
async function postSageSyncAction(
  action: 'sync-checks' | 'sync-bank-accounts' | 'sync-balances' | 'sync-facilities',
  organizationId: string,
  financialEntityId?: string,
): Promise<{ success: boolean; triggered: boolean; runId: string }> {
  const supabase = getSupabaseClient()
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('Authentication required - please log in')
  }

  // Abort the request if the edge function takes longer than 30s — guards
  // against an indefinite hang when sage-sync is unresponsive (default browser
  // timeout is multi-minute, which would block the mutation toast forever).
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), SAGE_SYNC_REQUEST_TIMEOUT_MS)

  let response: Response
  try {
    response = await fetch(`${getSupabaseUrl()}/functions/v1/sage-sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      // `financial_entity_id` is only forwarded when provided so the
      // sync-balances edge contract can distinguish a per-account sync
      // (key present) from a Resync All (key absent). Spreading a
      // conditional object keeps the key off the body entirely when
      // undefined rather than sending an explicit `null`.
      body: JSON.stringify({
        action,
        organizationId,
        ...(financialEntityId ? { financial_entity_id: financialEntityId } : {}),
      }),
      signal: controller.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Sage sync request timed out — try again in a moment')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const err = (await response.json()) as { error?: string }
      if (err?.error) message = err.error
    } catch (parseError) {
      // Production keeps the silent fallback (default `message` already says
      // "Request failed (NNN)") because end users don't need raw parse errors.
      // But in dev surface the cause so backend/proxy/format bugs aren't
      // masked — same pattern as the sortColumn warn in db-tenant.ts.
      if (import.meta.env.DEV) {
        console.warn('postSageSyncAction: failed to parse error response body', parseError)
      }
    }
    throw new Error(message)
  }

  // Wrap the success-path JSON parse in try/catch for symmetry with the
  // error-path parse above (the 2xx body could be truncated or malformed
  // too — proxy layer hiccup, premature stream close, edge function
  // returning HTML for a misrouted request, etc.). Without this, an
  // unhandled SyntaxError would propagate up into the mutation toast as
  // a confusing "Unexpected token < in JSON..." instead of a clean
  // "Failed to parse sage-sync response" the operator can actually triage.
  try {
    return (await response.json()) as { success: boolean; triggered: boolean; runId: string }
  } catch (parseError) {
    if (import.meta.env.DEV) {
      console.warn('postSageSyncAction: failed to parse success response body', parseError)
    }
    throw new Error('Failed to parse sage-sync response')
  }
}

/**
 * Poll tenant.sage_sync_state every 3s while any row has sync_status = 'syncing'.
 * Detects transitions to terminal states (completed/failed) and fires a toast.
 * On transition, invalidates downstream queries so dependent UI refreshes:
 *   - SAGE_KEYS.checks(orgId), SAGE_KEYS.bankAccounts(orgId) — the Sage caches
 *   - ['banking','facilities',orgId], ['banking','bank-accounts',orgId] — banking views
 *   - bare ['facilities'] — an org-agnostic key consumed by older facility lists;
 *     invalidates regardless of org.
 *
 * Returns the per-sync-type rows split out for convenient consumption:
 *   - checks / bankAccounts — the matching row or null
 *   - isSyncing — true when any row is currently 'syncing'
 *
 * TODO(test-coverage): unit tests for this hook are intentionally
 * deferred — see review 18216 issue #24. The polling logic is
 * real-timer + ref-based + multi-state, so a useful suite needs
 * `vi.useFakeTimers()` + a fake `fetch` queue + careful unmount
 * choreography to not flake on the 3s / 15s / 120s boundaries.
 *
 * Revisit when:
 *   (a) the hook regresses in production (we'd write the test against
 *       the actual failure mode rather than speculatively),
 *   (b) `usePollingWithCompletion<T>` is extracted (issue #28) — the
 *       tests should live with the abstraction, not the bespoke hook,
 *   (c) we wire up CI timing assertions on the 120s safety timeout.
 *
 * Until one of those triggers, the production safety net is the
 * 120s timeout fallback below; backend behaviour is also covered
 * indirectly by `useSageIntegration.test.tsx` exercising the
 * postSageSyncAction MSW handlers.
 */
export function useSageSyncStatus() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  /**
   * Map of sync_type -> sync_status from the previous render.
   * Used to detect transitions to terminal states (completed/failed)
   * regardless of what the previous state was (covers fast tasks
   * that skip from idle straight to completed between polls).
   */
  const prevStatusMapRef = useRef<Map<string, string>>(new Map())

  /**
   * Timestamp when polling started — used for the 120s safety timeout.
   * Null when not actively polling.
   */
  const pollingStartRef = useRef<number | null>(null)

  /** Whether the safety timeout has fired for the current polling session. */
  const timeoutFiredRef = useRef(false)

  /**
   * The `syncTriggerTime` value that the safety-timeout flag was last reset
   * against. Tracked so we only un-stick `timeoutFiredRef` ONCE per new sync
   * session — without this, every refetch would re-evaluate the reset
   * condition and pointlessly clear the flag on every poll.
   */
  const lastResetTriggerTimeRef = useRef<number | null>(null)

  /**
   * Skip transition detection on the first data load — rows may already
   * have terminal states (completed/failed) from a previous sync session.
   */
  const isFirstLoadRef = useRef(true)

  /**
   * Last orgId we observed — used to detect an org switch (user changes
   * active organization while the hook is mounted). On switch we must
   * reset `prevStatusMapRef` and `isFirstLoadRef` so the new org's first
   * emission isn't diffed against the previous org's status map (which
   * would fire spurious completion / failure toasts for unrelated syncs).
   * `useRef` instead of state so the comparison happens synchronously
   * inside the effect without an extra render.
   */
  const prevOrgIdRef = useRef<string | undefined>(orgId)

  const query = useQuery<SageSyncStateRow[]>({
    queryKey: SAGE_KEYS.syncStatus(orgId),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.sageSyncState.list()
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!orgId,
    /**
     * Refetch interval logic:
     *   - if any row is 'syncing' → poll every 3s (clear trigger time)
     *   - if no row is 'syncing' but a trigger fired recently → poll for the
     *     remainder of the 15s grace period
     *   - otherwise → don't poll
     *   - if the safety timeout fired, stop polling permanently
     */
    refetchInterval: (q) => {
      // New-session safety-timeout reset: if the safety timeout fired in
      // a previous session AND the user has since triggered a fresh sync
      // (`syncTriggerTime` is a newer timestamp than the one we last reset
      // against), un-stick the flag so polling can resume. Without this,
      // a single 120s timeout permanently disables polling for the
      // remaining lifetime of the hook instance — even though the
      // mutation's `invalidateQueries(SAGE_KEYS.syncStatus)` triggers
      // exactly one refetch (which lands here), so this branch gets the
      // chance to flip the flag before the `timeoutFiredRef.current
      // return false` short-circuit below.
      const triggerTime = queryClient.getQueryData<number>(SAGE_KEYS.syncTriggerTime(orgId))
      if (
        timeoutFiredRef.current &&
        triggerTime !== undefined &&
        triggerTime !== lastResetTriggerTimeRef.current
      ) {
        timeoutFiredRef.current = false
        pollingStartRef.current = null
        lastResetTriggerTimeRef.current = triggerTime
      }

      if (timeoutFiredRef.current) return false
      const rows = q.state.data
      if (!rows) return false

      // A fresh 'pending' row (edge claimed the sync, task not yet started)
      // counts as active just like a 'syncing' row, so polling keeps running
      // through the waiting window. Once a 'pending' row goes stale
      // (>PENDING_STALE_TIMEOUT_MS) it stops being active and this branch no
      // longer keeps the poll alive — the stale-pending handling in the effect
      // below then stops polling and surfaces the warning toast.
      const anySyncing = rows.some(isActiveSync)

      // Task is running (or freshly claimed) — clear the trigger timestamp and
      // poll normally
      if (anySyncing) {
        queryClient.removeQueries({ queryKey: SAGE_KEYS.syncTriggerTime(orgId) })
        return SYNC_POLLING_INTERVAL_MS
      }

      // Reaching here means nothing is actively syncing. A 'pending' row that
      // is no longer active has aged past PENDING_STALE_TIMEOUT_MS — the edge
      // claimed the sync but the Trigger.dev task never advanced it to
      // 'syncing' (queue back-pressure or a task that failed to enqueue). This
      // is the same "task never started" condition the trigger-time grace
      // branch below detects, but anchored to the row's own updated_at so it
      // works even without a fresh syncTriggerTime token (e.g. after a page
      // reload). Surface the existing warning once and stop polling.
      //
      // We reuse `timeoutFiredRef` as the single-fire guard: setting it makes
      // the `timeoutFiredRef.current` short-circuit at the top of this callback
      // return false on every subsequent poll, and the new-session reset logic
      // (also above) un-sticks it when the user triggers a fresh sync. We also
      // clear any leftover trigger token so the grace branch below can't fire a
      // second, duplicate warning.
      const anyStalePending = rows.some(
        (r) => r.sync_status === 'pending' && !isActiveSync(r),
      )
      if (anyStalePending) {
        timeoutFiredRef.current = true
        pollingStartRef.current = null
        queryClient.removeQueries({ queryKey: SAGE_KEYS.syncTriggerTime(orgId) })
        toast.warning(
          'Sage sync is taking longer than expected to start. Refresh the page in a few minutes to check status.',
        )
        return false
      }

      // No row is syncing yet, but a trigger was recently fired —
      // keep polling during the grace period so we catch when the task
      // starts. `triggerTime` was already read at the top of this
      // function for the safety-timeout reset; re-reading here would
      // shadow the binding, so we reuse it.
      if (triggerTime !== undefined) {
        const elapsed = Date.now() - triggerTime
        if (elapsed < TRIGGER_GRACE_PERIOD_MS) {
          return SYNC_POLLING_INTERVAL_MS
        }
        // Grace period expired without ever seeing an active row — the
        // `anySyncing` branch above would have cleared the trigger token
        // first if any row had gone active, so reaching here means the
        // Trigger.dev task never made it to the running state within the
        // 15s window. Toast a warning so the user doesn't silently wait
        // for an update that won't come (most common cause: Trigger.dev
        // queue back-pressure or a task that failed to enqueue). This
        // fires exactly once per session because `removeQueries` makes
        // `triggerTime` undefined on the next poll.
        queryClient.removeQueries({ queryKey: SAGE_KEYS.syncTriggerTime(orgId) })
        toast.warning(
          'Sage sync is taking longer than expected to start. Refresh the page in a few minutes to check status.',
        )
      }

      return false
    },
  })

  /** Memoize rows to maintain referential stability for the useEffect dep array */
  const rows = useMemo(() => query.data ?? [], [query.data])

  // A sync is "in progress" while any row is actively syncing OR freshly
  // pending (see isActiveSync). This is what disables the IntacctBalanceTable
  // buttons; a stale 'pending' row no longer counts, so the UI auto-frees.
  const isSyncing = rows.some(isActiveSync)

  // Handle polling timeout and sync completion transitions
  useEffect(() => {
    // Org switch reset: when `orgId` changes, the previous-status map and
    // first-load flag belong to the old org. Wipe them before any
    // transition diffing so the new org's first emission can't fire
    // toasts that reference the old org's sync session.
    if (prevOrgIdRef.current !== orgId) {
      prevStatusMapRef.current = new Map()
      isFirstLoadRef.current = true
      pollingStartRef.current = null
      timeoutFiredRef.current = false
      prevOrgIdRef.current = orgId
    }

    // Track polling start time for the safety timeout
    if (isSyncing && pollingStartRef.current === null) {
      pollingStartRef.current = Date.now()
      timeoutFiredRef.current = false
    }

    // Safety timeout: stop polling after 120s and warn the user
    if (isSyncing && pollingStartRef.current !== null && !timeoutFiredRef.current) {
      const elapsed = Date.now() - pollingStartRef.current
      if (elapsed >= SYNC_POLLING_TIMEOUT_MS) {
        timeoutFiredRef.current = true
        toast.error(
          'Sage sync is taking longer than expected. This may indicate an issue with the Sage integration. ' +
            'Try refreshing the page in a few minutes. If the problem persists, please contact support.',
        )
        pollingStartRef.current = null
        return
      }
    }

    // On first data load, snapshot statuses without firing transition toasts —
    // rows may already be in terminal states from a previous sync session.
    if (isFirstLoadRef.current && rows.length > 0) {
      isFirstLoadRef.current = false
      const initialMap = new Map<string, string>()
      for (const row of rows) {
        initialMap.set(row.sync_type, row.sync_status)
      }
      prevStatusMapRef.current = initialMap
      return
    }

    // Detect rows that just transitioned to a terminal state (completed/failed).
    const newlyCompleted: SageSyncStateRow[] = []
    const newlyFailed: SageSyncStateRow[] = []

    for (const row of rows) {
      const prevStatus = prevStatusMapRef.current.get(row.sync_type)
      if (row.sync_status === 'completed' && prevStatus !== 'completed') {
        newlyCompleted.push(row)
      }
      if (row.sync_status === 'failed' && prevStatus !== 'failed') {
        newlyFailed.push(row)
      }
    }

    const hasTransitions = newlyCompleted.length > 0 || newlyFailed.length > 0

    if (hasTransitions) {
      // Clear trigger timestamp and polling tracking
      queryClient.removeQueries({ queryKey: SAGE_KEYS.syncTriggerTime(orgId) })
      pollingStartRef.current = null
      timeoutFiredRef.current = false

      // Show toasts for transitions — phrase by sync_type so the user knows
      // which operation finished
      for (const row of newlyFailed) {
        toast.error(row.last_error || `Sage ${labelForSyncType(row.sync_type)} failed`)
      }
      for (const row of newlyCompleted) {
        toast.success(`Sage ${labelForSyncType(row.sync_type)} completed`)
      }

      // Invalidate downstream queries that depend on Sage data.
      // Includes the Outstanding Checks list, bank accounts catalog, and
      // banking-side caches that may JOIN onto sage tables.
      //
      // BANKING_KEYS.allKpi is invalidated on EVERY sync completion (not just
      // balances): a checks/bank-accounts sync also feeds the Outstanding
      // Checks and Book Balance tiles, and a balances sync feeds the new
      // Intacct Balance tile. Previously only mutations invalidated allKpi,
      // so a background sync left the dashboard tiles stale until the 5-minute
      // stale-time elapsed (DCP-159/160 KPI-invalidation gap). The sage bank
      // accounts query (SAGE_KEYS.bankAccounts) carries the per-account
      // intacct_balance columns the new table renders, so its invalidation
      // here doubles as the balances-table refresh.
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['banking', 'facilities', orgId] }),
        queryClient.invalidateQueries({ queryKey: ['facilities'] }),
        queryClient.invalidateQueries({ queryKey: SAGE_KEYS.checks(orgId) }),
        queryClient.invalidateQueries({ queryKey: SAGE_KEYS.bankAccounts(orgId) }),
        queryClient.invalidateQueries({ queryKey: ['banking', 'bank-accounts', orgId] }),
        queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi }),
        // A completed checks / bank-accounts / balances sync changes the
        // Outstanding Checks and Intacct Balance the Cash Position Summary
        // reads — refresh it too (DCP-143).
        queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY }),
      ])
    }

    // Reset polling tracking when no longer syncing (covers non-transition case)
    if (!isSyncing) {
      pollingStartRef.current = null
    }

    // Save current statuses for next comparison
    const nextMap = new Map<string, string>()
    for (const row of rows) {
      nextMap.set(row.sync_type, row.sync_status)
    }
    prevStatusMapRef.current = nextMap

    // Cleanup on unmount OR when `orgId` changes — reset the polling-clock
    // refs so a fresh org doesn't inherit the previous org's safety-timeout
    // countdown, and a session left mid-sync can't continue ticking after
    // the page unmounts. `prevStatusMapRef` is intentionally NOT reset:
    // it's compared against the new query's first emission so we don't
    // re-fire completion/failure toasts that already showed before unmount.
    // `isFirstLoadRef` is also preserved for the same reason.
    return () => {
      pollingStartRef.current = null
      timeoutFiredRef.current = false
    }
  }, [isSyncing, rows, queryClient, orgId])

  /** Convenience accessors split by sync_type — null when no row yet */
  const checks = rows.find((r) => r.sync_type === 'checks') ?? null
  const bankAccounts = rows.find((r) => r.sync_type === 'bank_accounts') ?? null
  // Intacct balances sync state (DCP-159/160). Exposes last_sync_at / status /
  // last_error for the "Last synced" indicator and per-account status badges
  // in IntacctBalanceTable.
  const balances = rows.find((r) => r.sync_type === 'balances') ?? null
  // Facilities auto-import sync state (DCP-262). Drives the Facilities card at
  // the top of the Sage Intacct settings page: status / last run / items synced.
  const facilities = rows.find((r) => r.sync_type === 'facilities') ?? null

  return {
    rows,
    checks,
    bankAccounts,
    balances,
    facilities,
    isSyncing,
    error: query.error,
  }
}

/**
 * Possible values for `sage_sync_state.sync_status` — mirrors the CHECK
 * constraint defined on the table in migration `20260505000000`.
 *
 * Exported so UI code that renders status badges / branches on the status
 * can import the same union the DB enforces, instead of redeclaring the
 * literal type inline at each call site (which would silently drift if we
 * ever extend the migration).
 *
 * 'pending' is the transient claim state set by the `sage-sync` edge function
 * the moment it accepts a sync, before the Trigger.dev task flips the row to
 * 'syncing' once it actually starts running.
 */
export type SageSyncStatus = 'idle' | 'pending' | 'syncing' | 'completed' | 'failed'

/**
 * Trigger the per-tenant Sage Intacct AP check register sync.
 * Edge function `sage-sync` with action `sync-checks` returns 409 when a
 * sync is already in flight; we surface that as an error toast.
 *
 * Records a trigger time so useSageSyncStatus polls eagerly during the
 * 15s grace period before the Trigger.dev task starts running.
 */
export function useSyncSageChecks() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected')
      }
      return postSageSyncAction('sync-checks', orgId)
    },
    onSuccess: () => {
      // Eager-poll grace window: useSageSyncStatus picks this up and starts
      // polling every 3s until the Trigger.dev task flips the row to 'syncing'.
      queryClient.setQueryData(SAGE_KEYS.syncTriggerTime(orgId), Date.now())
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.syncStatus(orgId) })
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.checks(orgId) })
      toast.success('Checks sync started')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start checks sync')
    },
  })
}

/**
 * Options for {@link useSageChecks} — all optional. When nothing is passed,
 * the hook returns the full check register (legacy behaviour used by the
 * Sage Intacct settings page to aggregate per-bank-account counts).
 */
export interface UseSageChecksOptions {
  /** 1-indexed page number; pair with `pageSize` for server-side pagination. */
  page?: number
  /** Rows per page; pair with `page`. Both must be set together to enable pagination. */
  pageSize?: number
  /**
   * Filter by sage_checks.state codes (e.g. ['A','S'] for outstanding rows only).
   * Accepts `readonly string[]` so callers can pass a `... as const` literal
   * (e.g. `OUTSTANDING_STATES`) directly without first copying it into a
   * mutable array on every render.
   */
  states?: readonly string[]
  /**
   * Filter by effective check status (Issued / Paid / Voided as shown in the
   * Status badge); several values are OR-ed. Undefined/empty = all statuses.
   */
  statuses?: readonly DcpCheckStatus[]
  /**
   * When true, keeps only outstanding (effectively issued, uncashed) checks —
   * the client mirror of the SQL outstanding predicate; see `sageChecks.list`.
   * No production caller passes it: OutstandingChecksPage lists every status
   * (narrowed with `statuses` instead), and the SageIntacctPage aggregator
   * needs paid + outstanding rows.
   */
  outstandingOnly?: boolean
  /**
   * Filter by tenant facility ids, applied directly on the denormalized
   * `sage_checks.facility_id` column (DCP-308). Empty array is treated as
   * "caller resolved zero facilities" → no rows (see sageChecks.list).
   */
  facilityIds?: string[]
  /** ISO date inclusive lower bound for `when_paid`. */
  dateFrom?: string
  /** ISO date inclusive upper bound for `when_paid`. */
  dateTo?: string
  /** Server-side sort column (whitelisted via SageCheckSortColumn). */
  sortColumn?: SageCheckSortColumn
  /** Server-side sort direction; default 'desc'. */
  sortDirection?: 'asc' | 'desc'
}

/** Filter subset shared between the list and count queries. */
type SageChecksFilterParams = Pick<
  UseSageChecksOptions,
  'states' | 'statuses' | 'outstandingOnly' | 'facilityIds' | 'dateFrom' | 'dateTo'
>

/**
 * Read the per-tenant Sage AP check register (tenant.sage_checks).
 * Default order: `when_paid` desc (nulls last), then `record_no` desc as a
 * tiebreaker — outstanding checks have null when_paid, so record_no drives
 * their displayed sequence. Pass `sortColumn` to override.
 *
 * Pagination: pass `{ page, pageSize }` to enable server-side `.range()`.
 *   The query key includes both so each page is cached independently.
 * State filter: pass `{ states: ['C'] }` to fetch only Posted rows.
 * Facility filter: pass `{ facilityIds: [...] }` — filtered directly on the
 *   denormalized `sage_checks.facility_id` column (DCP-308).
 * Date range: `{ dateFrom, dateTo }` filters by `when_paid` (Issued date).
 *
 * @returns React Query result containing SageCheckRow[] or an error
 */
export function useSageChecks(options: UseSageChecksOptions = {}) {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id
  const {
    page,
    pageSize,
    states,
    statuses,
    outstandingOnly,
    facilityIds,
    dateFrom,
    dateTo,
    sortColumn,
    sortDirection,
  } = options

  return useQuery<SageCheckRow[]>({
    // Include pagination + filter + sort args in the key so each variant has
    // an independent cache entry. React Query's prefix-based invalidation
    // (`SAGE_KEYS.checks(orgId)`) still hits all of them.
    //
    // Sort + dedupe the array filters before keying so two callers that
    // picked the same set in different orders (`['A','C']` vs `['C','A']`)
    // hit the same cache entry instead of duplicating the fetch. Same
    // pattern as `BANKING_KEYS.balanceImportStatus` in `useBanking.ts`.
    // The downstream RPC doesn't care about element order — the filters
    // map to a PostgREST `.in(...)` clause which is set-semantic.
    queryKey: [
      ...SAGE_KEYS.checks(orgId),
      {
        page,
        pageSize,
        states: normalizeArrayForKey(states),
        statuses: normalizeArrayForKey(statuses),
        outstandingOnly,
        facilityIds: normalizeArrayForKey(facilityIds),
        dateFrom,
        dateTo,
        sortColumn,
        sortDirection,
      },
    ] as const,
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.sageChecks.list({
        page,
        pageSize,
        states,
        statuses,
        outstandingOnly,
        facilityIds,
        dateFrom,
        dateTo,
        sortColumn,
        sortDirection,
      })
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!orgId,
  })
}

/**
 * Total row count for tenant.sage_checks matching the same filters as the
 * list query. Used to compute pagination's totalPages without fetching all rows.
 */
export function useSageChecksCount(options: SageChecksFilterParams = {}) {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id
  const { states, statuses, outstandingOnly, facilityIds, dateFrom, dateTo } = options

  return useQuery<number>({
    // Mirror the normalisation from `useSageChecks` above — `states` and
    // `facilityIds` are sort+deduped via Set so two callers passing
    // the same filter set in different orders hit the same cache entry.
    // OutstandingChecksPage calls `useSageChecks` and `useSageChecksCount`
    // with identical filter args on the same render; without matching
    // normalisation here, a checkbox-order change would force the count
    // query to refetch even though the list query is a cache hit.
    queryKey: [
      ...SAGE_KEYS.checks(orgId),
      'count',
      {
        states: normalizeArrayForKey(states),
        statuses: normalizeArrayForKey(statuses),
        outstandingOnly,
        facilityIds: normalizeArrayForKey(facilityIds),
        dateFrom,
        dateTo,
      },
    ] as const,
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.sageChecks.count({
        states,
        statuses,
        outstandingOnly,
        facilityIds,
        dateFrom,
        dateTo,
      })
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!orgId,
  })
}

/** Row shape returned by the `sage_checks_aggregate_by_entity` RPC. */
export interface SageChecksAggregateRow {
  financial_entity: string
  total: number
  outstanding_count: number
  outstanding_amount: number
}

/**
 * Per-bank-account check aggregates computed server-side via the
 * `sage_checks_aggregate_by_entity` RPC. Returns one row per
 * `financial_entity` with total / outstanding count / outstanding amount.
 *
 * Why this instead of `useSageChecks()` + client-side aggregation:
 *   PostgREST is configured with `max_rows = 1000` (supabase/config.toml),
 *   so the unparameterised list query silently truncates the register on
 *   tenants with > 1k checks. The client-side aggregator was then computing
 *   per-entity counts from ≤10% of the data — older accounts whose rows
 *   didn't survive the truncation rendered as "0 checks" on
 *   /settings/sage. The RPC `GROUP BY`s in SQL and returns ≤ N rows
 *   (N = distinct bank accounts, typically tens), so the result is
 *   independent of row limits.
 *
 * Outstanding semantics match
 * `OUTSTANDING_CHECK_STATES` in apps/web/src/lib/sage-utils.ts AND the
 * `WHERE state = 'C' AND date_cashed IS NULL` clause inside the
 * SQL function (DCP-244: Posted-only). If one shifts, both must shift in
 * lockstep — otherwise the SageIntacctPage counters disagree with
 * /banking/outstanding-checks.
 *
 * @returns React Query result containing an array of aggregate rows. The
 * caller typically converts it to a Map keyed by financial_entity for O(1)
 * lookup in the bank-accounts coverage table.
 */
export function useSageChecksAggregateByEntity() {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useQuery<SageChecksAggregateRow[]>({
    // Same `SAGE_KEYS.checks(orgId)` prefix so the existing invalidation
    // calls in `useSyncSageChecks` / `useUpdateSageCheck` also refresh this
    // query without any extra plumbing.
    queryKey: [...SAGE_KEYS.checks(orgId), 'aggregate-by-entity'] as const,
    queryFn: async () => {
      const supabase = getSupabaseClient()
      if (!orgId) {
        throw new Error('No organization selected')
      }
      const { data, error } = await supabase.rpc('sage_checks_aggregate_by_entity', {
        org_id: orgId,
      })
      if (error) throw new Error(error.message)
      return data ?? []
    },
    enabled: !!orgId,
  })
}

/**
 * Mutation: atomically update a check's DCP Status override AND Date Cashed in a
 * single write (DCP-275). Replaces the former split useUpdateSageCheckStatus /
 * useUpdateSageCheckDateCashed hooks — the Outstanding Checks page now edits both
 * fields together via one row-level inline editor.
 *
 * Optimistically patches BOTH `dcp_check_status` and `date_cashed` on every
 * cached checks-list snapshot before the round-trip resolves, rolls back on
 * error, and invalidates on settle so the cache converges on server truth.
 *
 * Callers MUST pass the edited row's `sage_cleared` as `sageCleared` (DCP-320):
 * it decides whether an Issued save stores an explicit 'issued' override or
 * NULL ("follow Sage") — see `storedStatusFor`.
 *
 * Invalidates the entire SAGE_KEYS.checks(orgId) prefix so every paginated +
 * filtered + sorted variant in the cache picks up the change.
 */
export function useUpdateSageCheck() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async ({
      recordNo,
      status,
      dateCashed,
      sageCleared,
    }: {
      recordNo: number
      status: DcpCheckStatus
      dateCashed: string | null
      /** The edited row's `sage_cleared` flag — see storedStatusFor (DCP-320). */
      sageCleared: SageCheckRow['sage_cleared']
    }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.sageChecks.updateCheck(
        recordNo,
        status,
        dateCashed,
        sageCleared,
      )
      if (error) throw new Error(error.message)
      return data
    },
    /**
     * Optimistic update — patch the row's `dcp_check_status` AND `date_cashed`
     * in every cached list snapshot under the `SAGE_KEYS.checks(orgId)` prefix
     * BEFORE the network round-trip resolves so the edited cells reflect the
     * new values instantly on a slow connection.
     *
     * The date is resolved the same way the data layer resolves it — only 'paid'
     * carries a date; any other status clears it — so the optimistic snapshot
     * matches what the server will persist and effectiveCheckStatus() derives
     * the same badge before and after the refetch. The status is resolved the
     * same way too (`storedStatusFor`, DCP-320): a non-contradicting Issued is
     * patched in as NULL, which still derives Issued because the row's
     * sage_cleared is not 'T' and its date has just been cleared.
     *
     * Cache layout reminder:
     *   - List queries:  [..., 'checks', orgId, { ...filters }]   → SageCheckRow[]
     *   - Count queries: [..., 'checks', orgId, 'count', { ... }] → number
     *
     * `setQueriesData` matches both shapes (same prefix); the updater branches
     * on `Array.isArray(old)` so we only touch list entries and leave count
     * snapshots untouched. The original snapshot is captured in `previousEntries`
     * so `onError` below can roll back if the server rejects.
     */
    onMutate: async ({ recordNo, status, dateCashed, sageCleared }) => {
      await queryClient.cancelQueries({ queryKey: SAGE_KEYS.checks(orgId) })

      const previousEntries = queryClient.getQueriesData<SageCheckRow[] | number>({
        queryKey: SAGE_KEYS.checks(orgId),
      })

      // Mirror the server-side date resolution via the shared helper: only
      // 'paid' carries a date; any other status clears it.
      const resolvedDate = resolveDateCashed(status, dateCashed)
      // ...and the server-side status resolution (DCP-320).
      const storedStatus = storedStatusFor(status, sageCleared)

      queryClient.setQueriesData<SageCheckRow[] | number>(
        { queryKey: SAGE_KEYS.checks(orgId) },
        (old) => {
          if (!Array.isArray(old)) return old
          // Strict mismatch on `record_no` — the row's PK in this query.
          // Only the matching row is replaced; everything else keeps its
          // reference so React's diffing skips the unchanged rows.
          return old.map((row) =>
            row.record_no === recordNo
              ? { ...row, dcp_check_status: storedStatus, date_cashed: resolvedDate }
              : row,
          )
        },
      )

      return { previousEntries }
    },
    onError: (error: Error, _variables, context) => {
      // Roll back every cache entry we touched in onMutate. If something
      // outside the mutation already raced an update in between, the restore
      // overwrites it — that's acceptable because the next onSettled
      // invalidation below will refetch authoritative data.
      if (context?.previousEntries) {
        for (const [queryKey, data] of context.previousEntries) {
          queryClient.setQueryData(queryKey, data)
        }
      }
      toast.error(error.message || 'Failed to update check')
    },
    onSettled: () => {
      // Always refetch after the mutation resolves (success or failure) so the
      // cache converges on server truth — covers the case where the server
      // transforms the value before persisting (e.g. an audit trigger touching
      // `updated_at`).
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.checks(orgId) })
      // A status/date change flips a check between issued and paid/voided, which
      // alters which checks count as effectively-issued and directly changes the
      // Outstanding Checks total and Book Balance tiles driven by useBankingKpi.
      // Invalidate so the dashboard refetches immediately instead of waiting out
      // the 5-minute stale-time window.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      // The same issued/paid/voided flip changes the Outstanding Checks the
      // Cash Position Summary reads — refresh it too (DCP-143).
      void queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
    },
  })
}

/**
 * Trigger the per-tenant Sage Intacct bank account catalog sync.
 * Edge function `sage-sync` with action `sync-bank-accounts` returns 409 when a
 * sync is already in flight; we surface that as an error toast.
 *
 * Records a trigger time so useSageSyncStatus polls eagerly during the
 * 15s grace period before the Trigger.dev task starts running.
 */
export function useSyncSageBankAccounts() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected')
      }
      return postSageSyncAction('sync-bank-accounts', orgId)
    },
    onSuccess: () => {
      // Eager-poll grace window: useSageSyncStatus picks this up and starts
      // polling every 3s until the Trigger.dev task flips the row to 'syncing'.
      queryClient.setQueryData(SAGE_KEYS.syncTriggerTime(orgId), Date.now())
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.syncStatus(orgId) })
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.bankAccounts(orgId) })
      toast.success('Bank accounts sync started')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start bank accounts sync')
    },
  })
}

/**
 * Trigger the per-tenant Sage Intacct facilities auto-import (DCP-262).
 *
 * Edge function `sage-sync` with action `sync-facilities` pulls every Sage
 * Intacct location for the org into tenant.facilities (as Inactive rows with
 * source='sage_intacct'). Returns 409 when a facilities sync is already in
 * flight; we surface that as an error toast.
 *
 * Mirrors {@link useSyncSageBankAccounts}: records a trigger time so
 * useSageSyncStatus polls eagerly during the 15s grace period before the
 * Trigger.dev task starts running, and invalidates the facilities list so the
 * Settings → Facilities page reflects the newly imported rows once the task
 * lands. The terminal completion/failure toast + a second facilities-list
 * refresh are driven centrally by useSageSyncStatus when the 'facilities' row
 * flips to completed/failed.
 */
export function useSyncSageFacilities() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected')
      }
      return postSageSyncAction('sync-facilities', orgId)
    },
    onSuccess: () => {
      // Eager-poll grace window: useSageSyncStatus picks this up and starts
      // polling every 3s until the Trigger.dev task flips the row to 'syncing'.
      queryClient.setQueryData(SAGE_KEYS.syncTriggerTime(orgId), Date.now())
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.syncStatus(orgId) })
      // Refresh the facilities list so imported rows surface (AC#5). Both the
      // org-scoped banking key and the bare ['facilities'] key are invalidated
      // to match the completion handler in useSageSyncStatus.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.facilities(orgId) })
      void queryClient.invalidateQueries({ queryKey: ['facilities'] })
      toast.success('Facilities sync started')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start facilities sync')
    },
  })
}

/**
 * Trigger the per-tenant Sage Intacct GL balance sync (DCP-159/160).
 *
 * One mutation serves both flows of the edge contract:
 *   - per-account (AC#2): pass `{ financialEntityId }` → the edge function
 *     syncs ONLY that bank account.
 *   - Resync All (AC#3): pass `{}` (or nothing) → every account is synced.
 *
 * Returns 409 when a balances sync is already in flight; we surface that as
 * an error toast. Records a trigger time so useSageSyncStatus polls eagerly
 * during the 15s grace period before the Trigger.dev task starts running.
 */
export function useSyncIntacctBalances() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async (params: { financialEntityId?: string } = {}) => {
      if (!orgId) {
        throw new Error('No organization selected')
      }
      return postSageSyncAction('sync-balances', orgId, params.financialEntityId)
    },
    onSuccess: (_data, variables) => {
      // Eager-poll grace window: useSageSyncStatus picks this up and starts
      // polling sage_sync_state every 3s until the Trigger.dev task flips the
      // global 'balances' row to 'syncing'. Always set for both flows so the
      // Resync All progress / completion toast is driven correctly.
      queryClient.setQueryData(SAGE_KEYS.syncTriggerTime(orgId), Date.now())
      // A PER-ACCOUNT sync additionally needs the bank-accounts table to poll
      // so its row-level balance_sync_status flips drive the per-row spinner /
      // re-enable independently of the global poll. Only set on the per-account
      // flow (financialEntityId present); a Resync All leaves it unset so the
      // bank-accounts poll relies purely on rows actually going account-active.
      if (variables?.financialEntityId) {
        queryClient.setQueryData(SAGE_KEYS.bankAccountsSyncTriggerTime(orgId), Date.now())
      }
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.syncStatus(orgId) })
      void queryClient.invalidateQueries({ queryKey: SAGE_KEYS.bankAccounts(orgId) })
      // Distinct copy per flow so the operator knows whether they kicked off a
      // single-account sync or a full resync.
      toast.success(
        variables?.financialEntityId ? 'Balance sync started' : 'Balance resync started',
      )
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start balance sync')
    },
  })
}

/**
 * Read the per-tenant Sage bank account catalog (tenant.sage_bank_accounts).
 * Defaults to active rows still present in Sage — used to populate the
 * bank-account-mapping dropdown.
 *
 * @param options.activeOnly - default true; pass false to include archived /
 *   missing-in-Sage rows (e.g. for admin reconciliation views).
 */
export function useSageBankAccounts(options: { activeOnly?: boolean } = {}) {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id
  const activeOnly = options.activeOnly ?? true

  /**
   * Map of financial_entity_id -> balance_sync_status from the previous poll.
   * Drives per-account completion/failure transition detection (AC#4) — the
   * row-level analogue of useSageSyncStatus's prevStatusMapRef. Keyed by the
   * account PK so a sibling account's status change can't be mistaken for this
   * one's.
   */
  /**
   * Per-account snapshot taken on every poll: the lock status AND the moment of
   * the last real balance write.
   *
   * `intacct_balance_synced_at` is in here because the status alone cannot tell
   * a sync that WROTE something from one that ran and wrote nothing (DCP-299,
   * PR #181 review #1). A run whose account is skipped by the task — its Sage
   * location maps to no facility, or to none at all — leaves
   * `intacct_balance_status` at its previous `'synced'`, so comparing the status
   * reported success for a sync that did not happen. The timestamp only moves on
   * an actual write, so it is the honest signal. It is nullable and its CHECK
   * ('synced' | 'failed') could not carry a third state without a migration.
   */
  const prevAccountStatusMapRef = useRef<
    Map<string, { status: string; syncedAt: string | null }>
  >(new Map())

  /**
   * Skip transition detection on the first data load — rows may already carry
   * terminal balance statuses (synced/failed) from a previous sync session,
   * which would otherwise fire spurious toasts on mount.
   */
  const isFirstAccountLoadRef = useRef(true)

  /**
   * Last orgId observed — used to detect an org switch and reset the
   * transition-detection refs so the new org's first emission isn't diffed
   * against the previous org's account statuses (mirrors useSageSyncStatus).
   */
  const prevOrgIdRef = useRef<string | undefined>(orgId)

  const query = useQuery<SageBankAccountRow[]>({
    queryKey: [...SAGE_KEYS.bankAccounts(orgId), { activeOnly }] as const,
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.sageBankAccounts.list({ activeOnly })
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!orgId,
    /**
     * Per-account poll. Refetch sage_bank_accounts every 3s while:
     *   - ANY row is account-active (balance_sync_status syncing / fresh
     *     pending) — keeps the per-row spinner live until the task finishes, OR
     *   - a per-account sync was triggered within the last 15s grace window —
     *     covers the gap between the button click and the row's status flipping
     *     to 'pending'/'syncing' in DB.
     * Otherwise don't poll. Auto-stops: once nothing is active and the grace
     * window lapses, the interval returns false and the query goes idle. The
     * grace token is cleared the moment a row goes active so the 'active' branch
     * alone keeps the poll alive thereafter.
     */
    refetchInterval: (q) => {
      const rows = q.state.data
      if (!rows) return false

      const anyActive = rows.some(isAccountBalanceSyncActive)
      if (anyActive) {
        // A row is in-flight — clear the grace token (its job is done) and poll.
        queryClient.removeQueries({
          queryKey: SAGE_KEYS.bankAccountsSyncTriggerTime(orgId),
        })
        return SYNC_POLLING_INTERVAL_MS
      }

      // No row active yet — keep polling through the grace window so we catch
      // the row transitioning to 'pending'/'syncing' once the edge claim /
      // task pickup lands.
      const triggerTime = queryClient.getQueryData<number>(
        SAGE_KEYS.bankAccountsSyncTriggerTime(orgId),
      )
      if (triggerTime !== undefined) {
        if (Date.now() - triggerTime < TRIGGER_GRACE_PERIOD_MS) {
          return SYNC_POLLING_INTERVAL_MS
        }
        // Grace window lapsed without any row going active — drop the token so
        // the poll stops. The global useSageSyncStatus already surfaces the
        // "taking longer than expected" warning; we don't duplicate it here.
        queryClient.removeQueries({
          queryKey: SAGE_KEYS.bankAccountsSyncTriggerTime(orgId),
        })
      }

      return false
    },
  })

  /** Memoized rows for referential stability in the transition-detection effect. */
  const rows = useMemo(() => query.data ?? [], [query.data])

  // Per-account completion / failure toasts (AC#4). Detect a row transitioning
  // from account-active to a terminal data outcome (no longer active, with
  // intacct_balance_status 'synced' / 'failed') across polls and toast once.
  // The GLOBAL Resync All toast is owned by useSageSyncStatus — this only
  // covers the per-account Sync flow.
  useEffect(() => {
    // Org switch reset: wipe the per-account transition refs so the new org's
    // first emission can't be diffed against the previous org's statuses.
    if (prevOrgIdRef.current !== orgId) {
      prevAccountStatusMapRef.current = new Map()
      isFirstAccountLoadRef.current = true
      prevOrgIdRef.current = orgId
    }

    // First load: snapshot statuses without firing toasts — rows may already
    // be terminal from a previous session.
    if (isFirstAccountLoadRef.current && rows.length > 0) {
      isFirstAccountLoadRef.current = false
      const initialMap = new Map<string, { status: string; syncedAt: string | null }>()
      for (const row of rows) {
        initialMap.set(row.financial_entity_id, {
          status: row.balance_sync_status,
          syncedAt: row.intacct_balance_synced_at ?? null,
        })
      }
      prevAccountStatusMapRef.current = initialMap
      return
    }

    // Detect rows that just left the account-active state. A row transitions
    // when its previous balance_sync_status was active (syncing / pending) but
    // it is no longer account-active now AND the data outcome is terminal.
    let anyAccountSettled = false
    for (const row of rows) {
      const prev = prevAccountStatusMapRef.current.get(row.financial_entity_id)
      const wasActive = prev?.status === 'syncing' || prev?.status === 'pending'
      if (wasActive && !isAccountBalanceSyncActive(row)) {
        anyAccountSettled = true
        // Did this run actually write a balance? `intacct_balance_status` is
        // sticky — an account the task skipped keeps whatever it said before, so
        // a previously-'synced' account reported success for a run that touched
        // nothing (DCP-299, PR #181 review #1). The reachable skips: the
        // account's Sage location maps to NO facility, its `location_id` is
        // NULL, or the account is not eligible (missing in Sage / no GL account).
        // The deactivated-facility case is refused earlier with a 409, but this
        // check closes the others without having to enumerate the doors.
        const wrote =
          row.intacct_balance_synced_at != null &&
          row.intacct_balance_synced_at !== (prev?.syncedAt ?? null)
        if (row.intacct_balance_status === 'failed') {
          toast.error(`Balance sync failed for ${row.name}`)
        } else if (row.intacct_balance_status === 'synced' && wrote) {
          toast.success(`Balance synced for ${row.name}`)
        } else {
          // Everything else is a run that ended without writing a balance, and
          // it must SAY so rather than end in silence (PResto #11). Two shapes
          // reach here and the earlier `=== 'synced'` test only covered one:
          //
          //  - sticky 'synced' from an earlier run, unchanged by this one;
          //  - `intacct_balance_status` still NULL, because the column is
          //    nullable "until the account has been processed by the balances
          //    sync at least once" (20260608000000_sage_intacct_balances.sql)
          //    and a SKIPPED account is never processed. That is the FIRST-ever
          //    Sync on a freshly connected account whose Sage location maps to
          //    no facility, or to a deactivated one — precisely the case this
          //    ticket creates, and it produced no toast at all.
          //
          // An unconditional `else` also covers any future status value: an
          // unrecognised one means "we cannot claim this wrote a balance", which
          // is the safe reading for a figure the operator acts on.
          toast.error(
            `No balance was synced for ${row.name} — check its Sage location is set on a facility in Settings → Facilities.`,
          )
        }
      }
    }

    // A per-account sync never moves the global 'balances' sync row, so the
    // useSageSyncStatus completion path that refreshes the KPI tiles does not
    // fire for it. Refresh them here, once per poll, so the Intacct Balance and
    // Book vs. Intacct Variance tiles — and the Cash Position Summary, which
    // reads the same intacct_balance — pick up the new balance.
    if (anyAccountSettled) {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi }),
        queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY }),
      ])
    }

    // Save current statuses for the next comparison.
    const nextMap = new Map<string, { status: string; syncedAt: string | null }>()
    for (const row of rows) {
      nextMap.set(row.financial_entity_id, {
        status: row.balance_sync_status,
        syncedAt: row.intacct_balance_synced_at ?? null,
      })
    }
    prevAccountStatusMapRef.current = nextMap
  }, [rows, orgId, queryClient])

  return query
}
